import { ChangeEvent, FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import { API } from "../../api-services/ApiList";
import { storeToken, storeUser } from "../../api-services/Process";
import { SITELINKS } from "../../app-routes/links";
import { useGlobalState } from "../../context/GlobalStateContext";
import {
  FieldErrors,
  loginSchema,
  loginType,
  toFieldErrors,
} from "../../interface/auth.interface";
import { getErrorMessage } from "../../utils";
import AuthLayout from "./component/AuthLayout";
import {
  AuthHeading,
  FormAlert,
  PasswordField,
  SubmitButton,
  TextField,
} from "./component/FormControls";

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useGlobalState();

  // Message passed in from other screens, e.g. after a successful password reset
  const notice = (location.state as { notice?: string } | null)?.notice ?? "";

  // Sent here by the API client when the session expired (?reason=expired&next=/page)
  const query = new URLSearchParams(location.search);
  const expired = query.get("reason") === "expired";
  const rawNext = query.get("next") ?? "";
  // only same-site paths, never back to an auth screen
  const next =
    rawNext.startsWith("/") && !rawNext.startsWith("//") && !/^\/(sign-in|verify-sign-in|forgot-password|reset-password)/.test(rawNext)
      ? rawNext
      : "";

  const [form, setForm] = useState<loginType>({ email: "", password: "" });
  const [errors, setErrors] = useState<FieldErrors<loginType>>({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const key = name as keyof loginType;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError("");

    const result = loginSchema.safeParse(form);
    if (!result.success) {
      setErrors(toFieldErrors(result.error));
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      const res = await apiClient.post(API.LOGIN, result.data);
      dispatch({ type: "SET_EMAIL", payload: result.data.email });

      if (res.data?.requires_2fa) {
        navigate(SITELINKS.VERIFY_SIGNIN, { state: { fromLogin: true, next } });
        return;
      }

      // Fallback: if the API ever signs in without 2FA, don't leave the admin stuck
      if (res.data?.access_token) {
        storeToken(res.data.access_token);
        storeUser(res.data.user);
        navigate(next || SITELINKS.DASHBOARD);
        return;
      }

      setServerError(
        "Sign-in didn't complete. Try again, and contact a super admin if this keeps happening."
      );
    } catch (error) {
      setServerError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Sign in | HoneConnect Admin">
      <AuthHeading
        title="Admin Login"
        description="For administrators only. After your password, we'll email you a 6-digit code to confirm it's you."
      />

      {!serverError && <FormAlert tone="success" message={notice} />}
      {!serverError && !notice && expired && (
        <FormAlert tone="info" message="Your session expired. Please sign in again to continue." />
      )}
      <FormAlert tone="error" message={serverError} />

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <TextField
          id="email"
          name="email"
          type="email"
          label="Email address"
          placeholder="name@company.com"
          autoComplete="username"
          inputMode="email"
          value={form.email}
          onChange={handleChange}
          error={errors.email}
        />

        <PasswordField
          id="password"
          name="password"
          label="Password"
          placeholder="Enter your password"
          autoComplete="current-password"
          value={form.password}
          onChange={handleChange}
          error={errors.password}
        />

        <div className="pt-3">
          <SubmitButton loading={loading} loadingText="Signing in">
            Continue
          </SubmitButton>
        </div>
      </form>

      {/* self-service reset is off for admins (2FA codes go to the same inbox) */}
      <p className="mt-8 text-center text-sm text-honeConnectGray">
        Forgot your password? Contact your admin team to have it reset.
      </p>
    </AuthLayout>
  );
};

export default SignIn;
